import { ampli } from '@/ampli';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import { FeedbackToast } from '@/components/TransactionToast/FeedbackToast';

const PORTFOLIO_FEEDBACK_DISMISSED_KEY = 'portfolioFeedbackDismissed';
const SCROLL_TRIGGER_THRESHOLD_PX = 200;

function hasDismissedPortfolioFeedback(): boolean {
  if (typeof window === 'undefined') return true;
  return localStorage.getItem(PORTFOLIO_FEEDBACK_DISMISSED_KEY) === 'true';
}

function markPortfolioFeedbackDismissed(): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(PORTFOLIO_FEEDBACK_DISMISSED_KEY, 'true');
}

interface UsePortfolioFeedbackPromptOptions {
  /** When false, the hook is a no-op (e.g. on public portfolio views). */
  enabled: boolean;
}

interface UsePortfolioFeedbackPromptReturn {
  feedbackModalOpen: boolean;
  setFeedbackModalOpen: (open: boolean) => void;
  handleFeedbackModalClose: () => void;
}

/**
 * Shows a feedback toast on the Portfolio page once the user scrolls past
 * SCROLL_TRIGGER_THRESHOLD_PX. Fires at most once per page load and never
 * again after the user dismisses or submits (persisted in localStorage).
 */
export function usePortfolioFeedbackPrompt({
  enabled,
}: UsePortfolioFeedbackPromptOptions): UsePortfolioFeedbackPromptReturn {
  const triggeredRef = useRef(false);
  const toastIdRef = useRef<string | number | null>(null);
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    if (hasDismissedPortfolioFeedback()) return;

    const trigger = () => {
      if (triggeredRef.current) return;
      if (window.scrollY < SCROLL_TRIGGER_THRESHOLD_PX) return;

      triggeredRef.current = true;
      window.removeEventListener('scroll', trigger);

      ampli.feedbackToastDisplayed({ surface: 'portfolio' });

      toastIdRef.current = toast.custom(
        (id) => (
          <FeedbackToast
            title="How can we improve?"
            onClose={() => {
              markPortfolioFeedbackDismissed();
              ampli.feedbackToastDismissed({ surface: 'portfolio' });
              toast.dismiss(id);
              toastIdRef.current = null;
            }}
            onFeedbackClick={() => {
              ampli.feedbackToastClicked({ surface: 'portfolio' });
              ampli.feedbackModalDisplayed();
              toast.dismiss(id);
              toastIdRef.current = null;
              setFeedbackModalOpen(true);
            }}
          />
        ),
        { duration: Infinity, unstyled: true },
      );
    };

    window.addEventListener('scroll', trigger, { passive: true });
    return () => {
      window.removeEventListener('scroll', trigger);
      if (toastIdRef.current !== null) {
        toast.dismiss(toastIdRef.current);
        toastIdRef.current = null;
      }
    };
  }, [enabled]);

  const handleFeedbackModalClose = () => {
    markPortfolioFeedbackDismissed();
    setFeedbackModalOpen(false);
  };

  return {
    feedbackModalOpen,
    setFeedbackModalOpen,
    handleFeedbackModalClose,
  };
}
